import React, {
  ForwardedRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { cloneDeep } from 'lodash-es';
import TextBubbles from '@home/components/TextBubbles';
import { formatDisplayValue } from '@customComp/Chart/_config/_utils';
import {
  changeLegendLocation,
  resolveSeriesColorsFromLegendColors,
} from '@customComp/Chart/chartConfigs/legend/legendUtils';
import { setTooltipMaxSize } from '@customComp/Chart/chartConfigs/tooltip/config';
import Chart from '..';
import {
  ChartRef,
  BarChartProps,
  ChartOption,
} from '../_config/_interface';
import type { ITooltipParam } from '../chartConfigs/types';
import { LIGHT_COLOR_ARR } from '../_config/_constant';
import {
  horizontalBarTooltipFormatter,
  useHorizontalBarData,
  useHorizontalBarOptions,
} from '../chartConfigs/horizontalBarChartHooks';
import {
  adjustGridRightBySeries,
  setDataLabel,
  setHorizontalBarAxisName,
  setHorizontalBarDataZoom,
} from '../chartConfigs/plugs';
import { LegendPositionEnum } from '../chartConfigs/legend/enums';

/** ECharts 内部 grid 布局探测（对齐 BarChart handleChartRendered，避免 as any） */
type TChartLayoutProbe = {
  getDom?: () => HTMLElement | null | undefined;
  getModel?: () => {
    getComponent?: (
      name: string,
      index: number,
    ) => {
      coordinateSystem?: {
        getRect?: () => { width?: number; height?: number; };
      };
    } | undefined;
  };
};

const HorizontalBarChartInner: React.ForwardRefRenderFunction<ChartRef, BarChartProps> = (
  props,
  ref: ForwardedRef<ChartRef>,
) => {
  const {
    metricNames,
    dimensions,
    chartConfig,
    fieldInfos,
    isOnlyOneDimension: isOnlyOneDimensionProp,
    tooltipMinTopPx,
    onEvents: externalOnEvents,
  } = props;
  const { xAxisName, yAxisName, showDataLabel } = chartConfig || {};
  const hasYAxisName = Boolean(yAxisName);
  const chartRef = useRef<ChartRef>(null);
  const [chartLayoutRect, setChartLayoutRect] = useState<{
    width: number;
    height: number;
    gridWidth: number;
    gridHeight: number;
  }>({ width: 0, height: 0, gridWidth: 0, gridHeight: 0 });

  const instance = useCallback(
    () => chartRef?.current?.instance(),
    [chartRef],
  );

  useImperativeHandle(ref, () => ({
    instance,
  }));

  const isOnlyOneDimension = isOnlyOneDimensionProp ?? (dimensions?.length <= 1);

  // 与折线/柱状一致：displayConfig 用完整 metricName（含 t0_）做 key
  const displayConfig = useMemo(() => {
    const config: Record<string, { accuracy: number; unit: string; }> = {};
    metricNames.forEach((metricName: string) => {
      const { accuracy, unit } = fieldInfos?.find((fieldInfo) => fieldInfo.name === metricName) || {
        accuracy: 0,
        unit: 'bmk',
      };
      config[metricName] = { accuracy, unit };
    });
    return config;
  }, [fieldInfos, metricNames]);

  const {
    valueTypeMap,
    series,
    legendData,
    yAxisData,
  } = useHorizontalBarData({
    ...props,
    displayConfig,
  });

  const seriesColors = useMemo(() => {
    return resolveSeriesColorsFromLegendColors({
      legendData,
      legendColors: chartConfig?.legendColors,
      palette: LIGHT_COLOR_ARR,
    });
  }, [chartConfig?.legendColors, legendData]);

  const setOtherConfigFunc = useCallback((opt: ChartOption) => {
    let nextOption = cloneDeep(opt);
    nextOption.color = seriesColors;

    // 如果 多个指标中, 全是百分比类型, 则Y轴展示百分比
    if (
      !Object.values(valueTypeMap).some(type => type !== 'percent') &&
      Array.isArray(nextOption?.xAxis) &&
      nextOption?.xAxis?.[0]?.axisLabel
    ) {
      nextOption.xAxis[0].axisLabel.formatter = '{value}%';
    }
    // 设置X轴和Y轴名称
    if (xAxisName || yAxisName) {
      setHorizontalBarAxisName(nextOption, chartConfig);
    }
    // 如果y轴超过20个展示，设置dataZoom组件
    if (nextOption?.yAxis?.data?.length > 20) {
      setHorizontalBarDataZoom(nextOption);
    }
    // 设置数据标签
    setDataLabel(nextOption, chartConfig, {
      labelAuto: false,
      isNewline: false,
      isLabelVisible: false,
      label: {
        position: 'right',
        resversePosition: 'insideLeft',
        align: 'left',
        resverseAlign: 'left',
      },
      metricFormatter: (param, isPercent) => {
        // 与折线/柱状一致：用 data.metric（完整 id）查 displayConfig
        const { accuracy, unit } = displayConfig[param?.data?.metric || ''] || {
          accuracy: 0,
          unit: 'bmk',
        };
        return formatDisplayValue({
          value: param.value,
          isPercent,
          accuracy,
          unit,
        });
      },
    });
    // 设置 tooltip 最大尺寸（对齐 BarChart）
    nextOption = setTooltipMaxSize(nextOption, chartConfig, { chartLayoutRect });
    // 设置柱状图展示标签，设置图形右边距偏移（与折线等一致：只认新字段 showDataLabel）
    if (showDataLabel) {
      adjustGridRightBySeries(nextOption, chartConfig, instance?.());
    }
    // 设置图例位置
    changeLegendLocation({
      option: nextOption,
      position: chartConfig?.legendPosition ?? LegendPositionEnum.Below,
    });
    return nextOption;
  }, [
    chartConfig,
    chartLayoutRect,
    displayConfig,
    instance,
    seriesColors,
    showDataLabel,
    valueTypeMap,
    xAxisName,
    yAxisName,
  ]);

  const handleChartRendered = useCallback((): void => {
    const chartInstance = instance?.() as TChartLayoutProbe | null | undefined;
    const instanceRect = chartInstance?.getDom?.()?.getBoundingClientRect?.();
    const width = instanceRect?.width ?? 400;
    const height = instanceRect?.height ?? 379;
    const gridRect = chartInstance?.getModel?.()?.getComponent?.('grid', 0)
      ?.coordinateSystem?.getRect?.();
    const gridWidth = gridRect?.width ?? 400;
    const gridHeight = gridRect?.height ?? 379;
    setChartLayoutRect((prev) => {
      // 图表宽高不变时保持 state 引用不变，避免重渲染 → Chart setOption → rendered 死循环
      if (prev.width === width && prev.height === height) {
        return prev;
      }
      return { width, height, gridWidth, gridHeight };
    });
  }, [instance]);

  const onEvents = useMemo(() => {
    const events: NonNullable<BarChartProps['onEvents']> = {
      rendered: handleChartRendered,
      finished: handleChartRendered,
      ...(externalOnEvents ?? {}),
    };
    return events;
  }, [externalOnEvents, handleChartRendered]);

  const option = useHorizontalBarOptions({
    valueTypeMap,
    series,
    legendData,
    yAxisData,
    tooltipMinTopPx,
    tooltipFormatter: (params: ITooltipParam[]) => horizontalBarTooltipFormatter({
      params,
      metricNames,
      isOnlyOneDimension,
      valueTypeMap,
      chartRef: chartRef?.current?.cRef,
      fieldInfos,
    }),
    setOtherConfigFunc,
  });

  return <div className="analysis-chart-container dbl-analysis-horizontal-bar-chart">
    <div
      className={classNames('dbl-charts-top-yAxis-text', {
        'dbl-charts-top-yAxis-text--hidden': !hasYAxisName,
      })}
    >
      <div className='dbh-text-exceeds-bubble-left'>
        <TextBubbles
          text={yAxisName}
        />
      </div>
    </div>
    <div className='analysis-chart-content'>
      <Chart ref={chartRef} option={option} notMerge={true} onEvents={onEvents} />
      <div className='dbh-text-exceeds-bubble-bottom'>
        <TextBubbles
          text={xAxisName}
        />
      </div>
    </div>
  </div>;
};

const HorizontalBarChart = React.forwardRef(HorizontalBarChartInner);

export default HorizontalBarChart;
